import React, { useEffect, useState } from 'react';
import { Col, Row, Select, Table } from 'antd';
import { Link } from 'react-router-dom';
import { api, endpoints } from '../../api';
import { TextbookCompletedSVG, TextbookDistributionSVG, TextbookExpertiseSVG } from '../../icons';
import { routerPath } from '../../router/routerPath';
import {
  BlueLayout,
  CustomButton,
  CustomSelect,
  CustomTableContainer,
  FormBlock,
  FormContainer,
  FormInput,
  GridItem,
  ManualContainer,
  PagesContainerUI,
  SvgFormer,
} from '../../styles/styled';
import { sortPublishers, sortStatus } from '../../utils/sortObject';
import { TableExpertiseType } from '../ExpertisePage/components/BooksExpertiseComponent/BooksExpertiseComponent';
import { LineOfTextbooksType } from '../ExpertisePage/types';
import { ExpertisesStatusType, PublishersType, SchoolClassesType, SchoolClassType } from '../NewApplicationPage/types';
import { PaginationType, TextbookSearchType, TextbooksResponseType, TextbooksTableType } from './types';

const { Option } = Select;

export const TextbookPage = () => {
  const [xlsLink, setXlsLink] = useState<string>();
  const [paginationState, setPaginationState] = useState<PaginationType>({
    pagination: 1,
    perPage: 10,
    total: 0,
  });

  const [searchState, setSearchState] = useState<TextbookSearchType>({});

  const textbookColumns = [
    {
      title: '№ п/п',
      dataIndex: 'table_number',
      key: 'table_number',
      render: (element: string, book: TableExpertiseType) => (
        <p>
          {(paginationState.pagination === 1 ? 0 : paginationState.pagination) * paginationState.perPage +
            (paginationState.pagination === 1 ? book.key + 1 : book.key)}
        </p>
      ),
    },
    {
      title: '№ заявления',
      dataIndex: 'line_of_textbooks',
      key: 'id',
      render: (line: LineOfTextbooksType) => {
        return (
          <Link to={routerPath.showApplication + line.application.id}>
            <p className="link">{line?.application?.registration_number || 'отсутствует'}</p>
          </Link>
        );
      },
    },
    {
      title: 'Наименование учебника',
      dataIndex: 'title',
      key: 'title',
      render: (value: string, book: TableExpertiseType) => {
        return (
          <Link to={routerPath.editTextbook + `/${book.id}`} style={{ textDecoration: 'underline' }}>
            {value}
          </Link>
        );
      },
    },
    {
      title: 'Класс',
      dataIndex: 'school_classes',
      key: 'school_classes',
      render: (value: SchoolClassType[]) => <p>{value[0]?.title}</p>,
    },
    {
      title: 'Автор (авторский коллектив)',
      dataIndex: 'author',
      key: 'author',
      render: (value: string) => <ManualContainer>{value}</ManualContainer>,
    },
    {
      title: 'Наименование учебного пособия',
      dataIndex: 'manual_title',
      key: 'manual_title',
      render: (value: string, book: TableExpertiseType) => {
        return book?.text_book_edu_man?.map(
          (el, idx) => el.title && <ManualContainer key={`${book.key}-${idx}`}>{el.title};</ManualContainer>,
        );
      },
    },
    {
      title: 'Автор (авторский коллектив) учебного пособия',
      dataIndex: 'manual_author',
      key: 'manual_author',
      render: (value: string, book: TableExpertiseType) => {
        return book?.text_book_edu_man?.map(
          (el, idx) => el.fio && <ManualContainer key={`${book.key}-${idx}`}>{el.fio};</ManualContainer>,
        );
      },
    },
    {
      title: 'Наименование издателя учебника',
      dataIndex: 'publisher',
      key: 'publisher',
      render: (_, book: TableExpertiseType) => <p>{book?.line_of_textbooks?.application?.publisher?.title}</p>,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (value: string, book: TableExpertiseType) => {
        return (
          <SvgFormer>
            {book.status?.code === 'distribution' ? (
              <TextbookDistributionSVG />
            ) : book.status?.code === 'expertise' ? (
              <TextbookExpertiseSVG />
            ) : book.status?.code === 'completed' ? (
              <TextbookCompletedSVG />
            ) : (
              book.status?.code
            )}
          </SvgFormer>
        );
      },
    },
  ];

  const [textbooksState, setTextbooks] = useState<TextbooksTableType>([]);
  const [schoolClassesState, setSchoolClasses] = useState<SchoolClassesType>([]);
  const [publishersState, setPublishers] = useState<PublishersType>([]);
  const [statusesState, setStatuses] = useState<ExpertisesStatusType>([]);
  const [loadState, setLoad] = useState<'search' | 'clear' | 'neutral'>('clear');

  useEffect(() => {
    (async () => {
      try {
        if (loadState === 'clear') {
          const response = await api.get<TextbooksResponseType>(
            endpoints.getTextbooks({ page: paginationState.pagination - 1, perPage: 10 }),
          );

          setPaginationState((prevState) => {
            return { ...prevState, total: response.data.count };
          });

          setTextbooks(
            response.data.textbooks.map((el, index) => {
              return { ...el, key: index };
            }),
          );
        } else if (loadState === 'search') {
          const response = await api.get<TextbooksResponseType>(
            endpoints.getTextbooks({ page: paginationState.pagination - 1, perPage: 9999, ...searchState }),
          );

          setPaginationState((prevState) => {
            return { ...prevState, pagination: 1, total: response.data.textbooks.length };
          });
          setLoad('neutral');

          setTextbooks(
            response.data.textbooks.map((el, index) => {
              return { ...el, key: index };
            }),
          );
        }
      } catch (err) {}
    })();
    // eslint-disable-next-line
  }, [paginationState.pagination, loadState]);

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get<SchoolClassesType>(endpoints.getSchoolClasses());
        const publishersResponse = await api.get<PublishersType>(endpoints.getPublishers(0, 1000));
        const expertiseStatuses = await api.get<ExpertisesStatusType>(endpoints.getTextbookStatuses());
        const xlsLinkResponse = await api.get<{ xls_file_path: string }>(endpoints.getExpertiseListsXls({}));

        if (!xlsLink) setXlsLink(xlsLinkResponse.data.xls_file_path);

        setStatuses(expertiseStatuses.data.sort(sortStatus));
        setPublishers(publishersResponse.data.sort(sortPublishers));
        setSchoolClasses(response.data);
      } catch (err) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateExpertiseListXls = async () => {
    try {
      const response = await api.get<{ xls_file_path: string }>(endpoints.getExpertiseListsXls(searchState));

      if (response) {
        const link = document.createElement('a');
        link.href = '/uploads/files/tmp/xls/expertise_lists.xls';
        link.setAttribute('download', 'expertise_lists.xls');
        document.body.appendChild(link);
        link.click();
      }
    } catch (err) {}
  };

  return (
    <PagesContainerUI>
      <BlueLayout>Учебники</BlueLayout>
      <FormBlock>
        <FormContainer>
          <Row gutter={[15, 20]}>
            <Col span={12}>
              <div className="text">Наименование учебника</div>
              <FormInput
                value={searchState?.name}
                onChange={(e) => setSearchState({ ...searchState, name: e.target.value })}
              />
            </Col>
            <Col span={12}>
              <div className="text">Класс</div>
              <CustomSelect
                onChange={(value, option) => {
                  setSearchState({
                    ...searchState,
                    school_classes: parseInt(Object.values(option)[0]),
                  });
                }}
                value={
                  searchState?.school_classes
                    ? schoolClassesState.find((el) => parseInt(el.id) === searchState.school_classes)?.title
                    : 'Все'
                }
                showSearch
              >
                <Option key={undefined} value={undefined}>
                  Все
                </Option>
                {schoolClassesState.map((el) => {
                  return (
                    <Option key={el.id} value={el.title}>
                      {el.title}
                    </Option>
                  );
                })}
              </CustomSelect>
            </Col>
            <Col span={12}>
              <div className="text">Автор (авторский коллектив)</div>
              <FormInput
                value={searchState?.author}
                onChange={(e) => setSearchState({ ...searchState, author: e.target.value })}
              />
            </Col>
            <Col span={12}>
              <div className="text">Наименование издательства</div>
              <CustomSelect
                onChange={(value, option) => {
                  setSearchState({
                    ...searchState,
                    publisher: parseInt(Object.values(option)[0]),
                  });
                }}
                value={
                  searchState?.publisher ? publishersState.find((el) => el.id === searchState.publisher)?.title : 'Все'
                }
                showSearch
              >
                <Option key={undefined} value={undefined}>
                  Все
                </Option>
                {publishersState.map((el) => {
                  return (
                    <Option key={el.id} value={el.title}>
                      {el.title}
                    </Option>
                  );
                })}
              </CustomSelect>
            </Col>
            <Col span={12}>
              <div className="text">Статус</div>
              <CustomSelect
                onChange={(value, option) => {
                  setSearchState({
                    ...searchState,
                    status: parseInt(Object.values(option)[0]),
                  });
                }}
                value={searchState?.status ? statusesState?.find((el) => el.id === searchState.status)?.name : 'Все'}
                showSearch
              >
                <Option key={undefined} value={undefined}>
                  Все
                </Option>
                {statusesState.map((el) => {
                  return (
                    <Option key={el.id} value={el.name}>
                      {el.name}
                    </Option>
                  );
                })}
              </CustomSelect>
            </Col>
            <Col span={24}>
              <div className="center">
                <CustomButton onClick={() => setLoad('search')}>Найти</CustomButton>
                <CustomButton
                  onClick={() => {
                    setLoad('clear');
                    setSearchState({});
                  }}
                  lined
                >
                  Очистить
                </CustomButton>
              </div>
            </Col>
            <Col span={24}>
              <div className="text red-star">Год поступления материала</div>
              <CustomSelect
                value={searchState.year ? searchState.year : 'Все'}
                onChange={(value) => {
                  setSearchState((prevState) => {
                    return { ...prevState, year: value as string };
                  });
                }}
              >
                <Option key={undefined} value={undefined}>
                  Все
                </Option>
                <Option key={2020} value={2020}>
                  2020
                </Option>
                <Option key={2021} value={2021}>
                  2021
                </Option>
                <Option key={2022} value={2022}>
                  2022
                </Option>
                <Option key={2023} value={2023}>
                  2023
                </Option>
                <Option key={2024} value={2024}>
                  2024
                </Option>
                <Option key={2025} value={2025}>
                  2025
                </Option>
              </CustomSelect>
            </Col>
            <GridItem align="flex-end" padding="0" justify="flex-end">
              {/*<a href={'/uploads/files/tmp/xls/expertise_lists.xls'} download>*/}
              <CustomButton onClick={updateExpertiseListXls} lined width="300px">
                Скачать свод оценок
              </CustomButton>
            </GridItem>
          </Row>
          <br />
        </FormContainer>
      </FormBlock>
      <CustomTableContainer>
        <Table
          pagination={{
            pageSize: 10,
            total: paginationState.total,
            current: paginationState.pagination,

            onChange: (value, pageSize) =>
              setPaginationState({ ...paginationState, perPage: pageSize, pagination: value }),
          }}
          columns={textbookColumns}
          dataSource={textbooksState}
          className={'table'}
        ></Table>
      </CustomTableContainer>
    </PagesContainerUI>
  );
};
